import { JsonProperty, JsonObject } from 'json2typescript';
import { ModelObject } from '@hermes/api-model-core';
import { Asset } from '@hermes/api-model-asset';

/******************************************************************************
Copyright (c) Microsoft Corporation.

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.
***************************************************************************** */
/* global Reflect, Promise, SuppressedError, Symbol, Iterator */


function __decorate(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}

function __metadata(metadataKey, metadataValue) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(metadataKey, metadataValue);
}

typeof SuppressedError === "function" ? SuppressedError : function (error, suppressed, message) {
    var e = new Error(message);
    return e.name = "SuppressedError", e.error = error, e.suppressed = suppressed, e;
};

/**
 * A simple key-value object to store attributes.
 * All these attributes are used by Simple Products and Beltkits Products
 */
let AbstractProductAttributes = class AbstractProductAttributes {
    constructor() {
        this.metalTitration1 = undefined;
        this.metalTitration2 = undefined;
        this.metalTitration3 = undefined;
        this.description = undefined;
        this.designer = undefined;
        this.designerLabel = undefined;
        this.dimensions = undefined;
        this.shippingInformation = undefined;
        this.colorHermes = undefined;
        // Also used in Analytics
        this.colorHermesCode = undefined;
        this.colorHermesLabel = undefined;
        this.ecoTax = undefined;
        this.sizeLabel = undefined;
    }
};
__decorate([
    JsonProperty("metalTitration1", String, true),
    __metadata("design:type", String)
], AbstractProductAttributes.prototype, "metalTitration1", undefined);
__decorate([
    JsonProperty("metalTitration2", String, true),
    __metadata("design:type", String)
], AbstractProductAttributes.prototype, "metalTitration2", undefined);
__decorate([
    JsonProperty("metalTitration3", String, true),
    __metadata("design:type", String)
], AbstractProductAttributes.prototype, "metalTitration3", undefined);
__decorate([
    JsonProperty("description", String, true),
    __metadata("design:type", String)
], AbstractProductAttributes.prototype, "description", undefined);
__decorate([
    JsonProperty("designer", String, true),
    __metadata("design:type", String)
], AbstractProductAttributes.prototype, "designer", undefined);
__decorate([
    JsonProperty("designerLabel", String, true),
    __metadata("design:type", String)
], AbstractProductAttributes.prototype, "designerLabel", undefined);
__decorate([
    JsonProperty("dimensions", String, true),
    __metadata("design:type", String)
], AbstractProductAttributes.prototype, "dimensions", undefined);
__decorate([
    JsonProperty("shippingInformation", String, true),
    __metadata("design:type", String)
], AbstractProductAttributes.prototype, "shippingInformation", undefined);
__decorate([
    JsonProperty("colorHermes", String, true),
    __metadata("design:type", String)
], AbstractProductAttributes.prototype, "colorHermes", undefined);
__decorate([
    JsonProperty("colorHermesCode", String, true),
    __metadata("design:type", String)
], AbstractProductAttributes.prototype, "colorHermesCode", undefined);
__decorate([
    JsonProperty("colorHermesLabel", String, true),
    __metadata("design:type", String)
], AbstractProductAttributes.prototype, "colorHermesLabel", undefined);
__decorate([
    JsonProperty("ecoTax", Number, true),
    __metadata("design:type", Number)
], AbstractProductAttributes.prototype, "ecoTax", undefined);
__decorate([
    JsonProperty("sizeLabel", String, true),
    __metadata("design:type", String)
], AbstractProductAttributes.prototype, "sizeLabel", undefined);
AbstractProductAttributes = __decorate([
    JsonObject("AbstractProductAttributes")
], AbstractProductAttributes);

/**
 * Provides storage and getters to indicate availability of a product.
 */
let ProductStock = class ProductStock extends ModelObject {
    constructor() {
        super(...arguments);
        this.ecom = undefined;
        this.retail = undefined;
        this.shared = undefined;
        this.hasVariantInEcomStock = undefined;
        this.displayOnly = undefined;
    }
};
__decorate([
    JsonProperty("ecom", Boolean, true),
    __metadata("design:type", Boolean)
], ProductStock.prototype, "ecom", undefined);
__decorate([
    JsonProperty("retail", Boolean, true),
    __metadata("design:type", Boolean)
], ProductStock.prototype, "retail", undefined);
__decorate([
    JsonProperty("shared", Boolean, true),
    __metadata("design:type", Boolean)
], ProductStock.prototype, "shared", undefined);
__decorate([
    JsonProperty("hasVariantInEcomStock", Boolean, true),
    __metadata("design:type", Boolean)
], ProductStock.prototype, "hasVariantInEcomStock", undefined);
__decorate([
    JsonProperty("displayOnly", Boolean, true),
    __metadata("design:type", Boolean)
], ProductStock.prototype, "displayOnly", undefined);
ProductStock = __decorate([
    JsonObject("ProductStock")
], ProductStock);

/**
 * A product can have collections of variants.
 * each variant has at least a sku.
 */
let AbstractProductVariant = class AbstractProductVariant {
    constructor() {
        this.sku = undefined;
        this.slug = undefined;
        this.stock = undefined;
        this.boxType = undefined;
    }
};
__decorate([
    JsonProperty("sku", String, true),
    __metadata("design:type", String)
], AbstractProductVariant.prototype, "sku", undefined);
__decorate([
    JsonProperty("slug", String, true),
    __metadata("design:type", String)
], AbstractProductVariant.prototype, "slug", undefined);
__decorate([
    JsonProperty("stock", ProductStock, true),
    __metadata("design:type", ProductStock)
], AbstractProductVariant.prototype, "stock", undefined);
__decorate([
    JsonProperty("boxType", String, true),
    __metadata("design:type", String)
], AbstractProductVariant.prototype, "boxType", undefined);
AbstractProductVariant = __decorate([
    JsonObject("AbstractProductVariant")
], AbstractProductVariant);

/**
 * A simple key-value object to store attributes.
 * These attributes are only used for Apple Watch Products
 */
let AppleWatchProductAttributes = class AppleWatchProductAttributes extends AbstractProductAttributes {
    constructor() {
        super(...arguments);
        this.skuSet = undefined;
        this.skuStrap = undefined;
        this.skuGuizmo = undefined;
        this.colorHermesStrap = undefined;
        this.colorHermesCodeStrap = undefined;
        this.finishGuizmo = undefined;
        this.finishCodeGuizmo = undefined;
        this.modelSizeGuizmo = undefined;
        this.modelSizeCodeGuizmo = undefined;
        this.departmentCodeStrap = undefined;
        this.familyCodeStrap = undefined;
        this.productCodeStrap = undefined;
        this.divisionCodeStrap = undefined;
        this.dimensionStrap = undefined;
        this.departmentCodeGuizmo = undefined;
        this.familyCodeGuizmo = undefined;
        this.productCodeGuizmo = undefined;
        this.divisionCodeGuizmo = undefined;
        this.dimensionGuizmo = undefined;
        this.madeInStrap = undefined;
        this.objectTypeTitleGuizmo = undefined;
        this.objectTypeTitleStrap = undefined;
        this.sizeStrap = undefined;
    }
};
__decorate([
    JsonProperty("skuSet", String, true),
    __metadata("design:type", String)
], AppleWatchProductAttributes.prototype, "skuSet", undefined);
__decorate([
    JsonProperty("skuStrap", String, true),
    __metadata("design:type", String)
], AppleWatchProductAttributes.prototype, "skuStrap", undefined);
__decorate([
    JsonProperty("skuGuizmo", String, true),
    __metadata("design:type", String)
], AppleWatchProductAttributes.prototype, "skuGuizmo", undefined);
__decorate([
    JsonProperty("colorHermesStrap", String, true),
    __metadata("design:type", String)
], AppleWatchProductAttributes.prototype, "colorHermesStrap", undefined);
__decorate([
    JsonProperty("colorHermesCodeStrap", String, true),
    __metadata("design:type", String)
], AppleWatchProductAttributes.prototype, "colorHermesCodeStrap", undefined);
__decorate([
    JsonProperty("finishGuizmo", String, true),
    __metadata("design:type", String)
], AppleWatchProductAttributes.prototype, "finishGuizmo", undefined);
__decorate([
    JsonProperty("finishCodeGuizmo", String, true),
    __metadata("design:type", String)
], AppleWatchProductAttributes.prototype, "finishCodeGuizmo", undefined);
__decorate([
    JsonProperty("modelSizeGuizmo", String, true),
    __metadata("design:type", String)
], AppleWatchProductAttributes.prototype, "modelSizeGuizmo", undefined);
__decorate([
    JsonProperty("modelSizeCodeGuizmo", String, true),
    __metadata("design:type", String)
], AppleWatchProductAttributes.prototype, "modelSizeCodeGuizmo", undefined);
__decorate([
    JsonProperty("departmentCodeStrap", String, true),
    __metadata("design:type", String)
], AppleWatchProductAttributes.prototype, "departmentCodeStrap", undefined);
__decorate([
    JsonProperty("familyCodeStrap", String, true),
    __metadata("design:type", String)
], AppleWatchProductAttributes.prototype, "familyCodeStrap", undefined);
__decorate([
    JsonProperty("productCodeStrap", String, true),
    __metadata("design:type", String)
], AppleWatchProductAttributes.prototype, "productCodeStrap", undefined);
__decorate([
    JsonProperty("divisionCodeStrap", String, true),
    __metadata("design:type", String)
], AppleWatchProductAttributes.prototype, "divisionCodeStrap", undefined);
__decorate([
    JsonProperty("dimensionStrap", String, true),
    __metadata("design:type", String)
], AppleWatchProductAttributes.prototype, "dimensionStrap", undefined);
__decorate([
    JsonProperty("departmentCodeGuizmo", String, true),
    __metadata("design:type", String)
], AppleWatchProductAttributes.prototype, "departmentCodeGuizmo", undefined);
__decorate([
    JsonProperty("familyCodeGuizmo", String, true),
    __metadata("design:type", String)
], AppleWatchProductAttributes.prototype, "familyCodeGuizmo", undefined);
__decorate([
    JsonProperty("productCodeGuizmo", String, true),
    __metadata("design:type", String)
], AppleWatchProductAttributes.prototype, "productCodeGuizmo", undefined);
__decorate([
    JsonProperty("divisionCodeGuizmo", String, true),
    __metadata("design:type", String)
], AppleWatchProductAttributes.prototype, "divisionCodeGuizmo", undefined);
__decorate([
    JsonProperty("dimensionGuizmo", String, true),
    __metadata("design:type", String)
], AppleWatchProductAttributes.prototype, "dimensionGuizmo", undefined);
__decorate([
    JsonProperty("madeInStrap", String, true),
    __metadata("design:type", String)
], AppleWatchProductAttributes.prototype, "madeInStrap", undefined);
__decorate([
    JsonProperty("objectTypeTitleGuizmo", String, true),
    __metadata("design:type", String)
], AppleWatchProductAttributes.prototype, "objectTypeTitleGuizmo", undefined);
__decorate([
    JsonProperty("objectTypeTitleStrap", String, true),
    __metadata("design:type", String)
], AppleWatchProductAttributes.prototype, "objectTypeTitleStrap", undefined);
__decorate([
    JsonProperty("sizeStrap", String, true),
    __metadata("design:type", String)
], AppleWatchProductAttributes.prototype, "sizeStrap", undefined);
AppleWatchProductAttributes = __decorate([
    JsonObject("AppleWatchProductAttributes")
], AppleWatchProductAttributes);

/**
 * A simple key-value object to store attributes.
 * All these attributes are only used for Beltkit Products
 */
let BeltkitProductAttributes = class BeltkitProductAttributes extends AbstractProductAttributes {
    constructor() {
        super(...arguments);
        /** Sku of set in case the product is a set */
        this.skuSet = undefined;
        /** SKU of buckle in case the product is a beltkit */
        this.skuBuckle = undefined;
        /** SKU of leather in case the product is a beltkit */
        this.skuLeather = undefined;
        /** Color of buckle in case the product is a beltkit */
        this.colorHermesBuckle = undefined;
        /**
         * Color code of buckle in case the product is a beltkit
         * Currently not used but might be for Analytics / SEO
         */
        this.colorHermesCodeBuckle = undefined;
        /** Display name list of buckle in case the product is a beltkit */
        this.displayNameList = undefined;
        /** Display name list label of buckle in case the product is a beltkit */
        this.displayNameListLabel = undefined;
        /** Color of leather in case the product is a beltkit */
        this.colorHermesLeather = undefined;
        /**
         * Color code of leather in case the product is a beltkit
         * Currently not used but might be for Analytics / SEO
         */
        this.colorHermesCodeLeather = undefined;
        /**
         * Ref Coloris of leather color
         */
        this.refColorisLeather = undefined;
        /** Department code of leather in case the product is a beltkit */
        this.departmentCodeLeather = undefined;
        /** Department code of buckle in case the product is a beltkit */
        this.departmentCodeBuckle = undefined;
        /** Family code of leather in case the product is a beltkit */
        this.familyCodeLeather = undefined;
        /** Family code of buckle in case the product is a beltkit */
        this.familyCodeBuckle = undefined;
        /** Product code of leather in case the product is a beltkit */
        this.productCodeLeather = undefined;
        /** Product code of buckle in case the product is a beltkit */
        this.productCodeBuckle = undefined;
        /** Division code of leather in case the product is a beltkit */
        this.divisionCodeLeather = undefined;
        /** Division code of buckle in case the product is a beltkit */
        this.divisionCodeBuckle = undefined;
        /** Finish label of buckle label in case the product is a beltkit */
        this.finishLabel = undefined;
        /** Finish code of buckle in case the product is a beltkit */
        this.finishCodeBuckle = undefined;
        /** Finish of buckle in case the product is a beltkit */
        this.finishBuckle = undefined;
        this.madeIn = undefined;
        this.madeInBuckle = undefined;
        this.objectTypeTitleBuckle = undefined;
        this.madeInLeather = undefined;
        this.objectTypeTitleLeather = undefined;
    }
};
__decorate([
    JsonProperty("skuSet", String, true),
    __metadata("design:type", String)
], BeltkitProductAttributes.prototype, "skuSet", undefined);
__decorate([
    JsonProperty("skuBuckle", String, true),
    __metadata("design:type", String)
], BeltkitProductAttributes.prototype, "skuBuckle", undefined);
__decorate([
    JsonProperty("skuLeather", String, true),
    __metadata("design:type", String)
], BeltkitProductAttributes.prototype, "skuLeather", undefined);
__decorate([
    JsonProperty("colorHermesBuckle", String, true),
    __metadata("design:type", String)
], BeltkitProductAttributes.prototype, "colorHermesBuckle", undefined);
__decorate([
    JsonProperty("colorHermesCodeBuckle", String, true),
    __metadata("design:type", String)
], BeltkitProductAttributes.prototype, "colorHermesCodeBuckle", undefined);
__decorate([
    JsonProperty("displayNameList", String, true),
    __metadata("design:type", String)
], BeltkitProductAttributes.prototype, "displayNameList", undefined);
__decorate([
    JsonProperty("displayNameListLabel", String, true),
    __metadata("design:type", String)
], BeltkitProductAttributes.prototype, "displayNameListLabel", undefined);
__decorate([
    JsonProperty("colorHermesLeather", String, true),
    __metadata("design:type", String)
], BeltkitProductAttributes.prototype, "colorHermesLeather", undefined);
__decorate([
    JsonProperty("colorHermesCodeLeather", String, true),
    __metadata("design:type", String)
], BeltkitProductAttributes.prototype, "colorHermesCodeLeather", undefined);
__decorate([
    JsonProperty("refColorisLeather", String, true),
    __metadata("design:type", String)
], BeltkitProductAttributes.prototype, "refColorisLeather", undefined);
__decorate([
    JsonProperty("departmentCodeLeather", String, true),
    __metadata("design:type", String)
], BeltkitProductAttributes.prototype, "departmentCodeLeather", undefined);
__decorate([
    JsonProperty("departmentCodeBuckle", String, true),
    __metadata("design:type", String)
], BeltkitProductAttributes.prototype, "departmentCodeBuckle", undefined);
__decorate([
    JsonProperty("familyCodeLeather", String, true),
    __metadata("design:type", String)
], BeltkitProductAttributes.prototype, "familyCodeLeather", undefined);
__decorate([
    JsonProperty("familyCodeBuckle", String, true),
    __metadata("design:type", String)
], BeltkitProductAttributes.prototype, "familyCodeBuckle", undefined);
__decorate([
    JsonProperty("productCodeLeather", String, true),
    __metadata("design:type", String)
], BeltkitProductAttributes.prototype, "productCodeLeather", undefined);
__decorate([
    JsonProperty("productCodeBuckle", String, true),
    __metadata("design:type", String)
], BeltkitProductAttributes.prototype, "productCodeBuckle", undefined);
__decorate([
    JsonProperty("divisionCodeLeather", String, true),
    __metadata("design:type", String)
], BeltkitProductAttributes.prototype, "divisionCodeLeather", undefined);
__decorate([
    JsonProperty("divisionCodeBuckle", String, true),
    __metadata("design:type", String)
], BeltkitProductAttributes.prototype, "divisionCodeBuckle", undefined);
__decorate([
    JsonProperty("finishLabel", String, true),
    __metadata("design:type", String)
], BeltkitProductAttributes.prototype, "finishLabel", undefined);
__decorate([
    JsonProperty("finishCodeBuckle", String, true),
    __metadata("design:type", String)
], BeltkitProductAttributes.prototype, "finishCodeBuckle", undefined);
__decorate([
    JsonProperty("finishBuckle", String, true),
    __metadata("design:type", String)
], BeltkitProductAttributes.prototype, "finishBuckle", undefined);
__decorate([
    JsonProperty("madeIn", String, true),
    __metadata("design:type", String)
], BeltkitProductAttributes.prototype, "madeIn", undefined);
__decorate([
    JsonProperty("madeInBuckle", String, true),
    __metadata("design:type", String)
], BeltkitProductAttributes.prototype, "madeInBuckle", undefined);
__decorate([
    JsonProperty("objectTypeTitleBuckle", String, true),
    __metadata("design:type", String)
], BeltkitProductAttributes.prototype, "objectTypeTitleBuckle", undefined);
__decorate([
    JsonProperty("madeInLeather", String, true),
    __metadata("design:type", String)
], BeltkitProductAttributes.prototype, "madeInLeather", undefined);
__decorate([
    JsonProperty("objectTypeTitleLeather", String, true),
    __metadata("design:type", String)
], BeltkitProductAttributes.prototype, "objectTypeTitleLeather", undefined);
BeltkitProductAttributes = __decorate([
    JsonObject("BeltkitProductAttributes")
], BeltkitProductAttributes);

/**
 * This interface is used to describe product of CrossSelling response.
 */
let CrossSellingProduct = class CrossSellingProduct extends ModelObject {
    constructor() {
        super(...arguments);
        /** Product color */
        this.avgColor = undefined;
        /** Is colorless */
        this.isColorless = false;
        /** Product category */
        this.categories = undefined;
        /** Product department code */
        this.departmentCode = undefined;
        /** Product dimension */
        this.dimensions = undefined;
        /** Product division code */
        this.divisionCode = undefined;
        /** Product family code */
        this.familyCode = undefined;
        /** Product image */
        this.image = undefined;
        /** Product mass */
        this.mass = undefined;
        /** Product mass & volume (for Fragrance & soap) */
        this.massVolume = undefined;
        /** Indicate if there is more colors */
        this.moreColors = false;
        /** Product price */
        this.price = undefined;
        /** Product product code */
        this.productCode = undefined;
        /** Product raw price */
        this.rawPrice = undefined;
        /** Product ref size */
        this.refSize = undefined;
        /** Product second id */
        this.secondId = undefined;
        /** Product sku */
        this.sku = undefined;
        /** Product title */
        this.title = undefined;
        /** Product url */
        this.url = undefined;
        /** Product stock availability */
        this.hasStock = undefined;
        /** Indicates if it is a personalize product */
        this.personalize = false;
        /** Customizable product type */
        this.persoProductType = undefined;
    }
};
__decorate([
    JsonProperty("avgColor", String, true),
    __metadata("design:type", String)
], CrossSellingProduct.prototype, "avgColor", undefined);
__decorate([
    JsonProperty("isColorless", Boolean, true),
    __metadata("design:type", Boolean)
], CrossSellingProduct.prototype, "isColorless", undefined);
__decorate([
    JsonProperty("categories", [String], true),
    __metadata("design:type", Array)
], CrossSellingProduct.prototype, "categories", undefined);
__decorate([
    JsonProperty("departmentCode", String, true),
    __metadata("design:type", String)
], CrossSellingProduct.prototype, "departmentCode", undefined);
__decorate([
    JsonProperty("dimensions", String, true),
    __metadata("design:type", String)
], CrossSellingProduct.prototype, "dimensions", undefined);
__decorate([
    JsonProperty("divisionCode", String, true),
    __metadata("design:type", String)
], CrossSellingProduct.prototype, "divisionCode", undefined);
__decorate([
    JsonProperty("familyCode", String, true),
    __metadata("design:type", String)
], CrossSellingProduct.prototype, "familyCode", undefined);
__decorate([
    JsonProperty("image", String, true),
    __metadata("design:type", String)
], CrossSellingProduct.prototype, "image", undefined);
__decorate([
    JsonProperty("mass", String, true),
    __metadata("design:type", String)
], CrossSellingProduct.prototype, "mass", undefined);
__decorate([
    JsonProperty("massVolume", String, true),
    __metadata("design:type", String)
], CrossSellingProduct.prototype, "massVolume", undefined);
__decorate([
    JsonProperty("moreColors", Boolean, true),
    __metadata("design:type", Boolean)
], CrossSellingProduct.prototype, "moreColors", undefined);
__decorate([
    JsonProperty("price", Number, true),
    __metadata("design:type", Number)
], CrossSellingProduct.prototype, "price", undefined);
__decorate([
    JsonProperty("productCode", String, true),
    __metadata("design:type", String)
], CrossSellingProduct.prototype, "productCode", undefined);
__decorate([
    JsonProperty("rawPrice", Number, true),
    __metadata("design:type", Number)
], CrossSellingProduct.prototype, "rawPrice", undefined);
__decorate([
    JsonProperty("refSize", String, true),
    __metadata("design:type", String)
], CrossSellingProduct.prototype, "refSize", undefined);
__decorate([
    JsonProperty("secondId", String, true),
    __metadata("design:type", String)
], CrossSellingProduct.prototype, "secondId", undefined);
__decorate([
    JsonProperty("sku", String, true),
    __metadata("design:type", String)
], CrossSellingProduct.prototype, "sku", undefined);
__decorate([
    JsonProperty("title", String, true),
    __metadata("design:type", String)
], CrossSellingProduct.prototype, "title", undefined);
__decorate([
    JsonProperty("url", String, true),
    __metadata("design:type", String)
], CrossSellingProduct.prototype, "url", undefined);
__decorate([
    JsonProperty("hasStock", Boolean, true),
    __metadata("design:type", Boolean)
], CrossSellingProduct.prototype, "hasStock", undefined);
__decorate([
    JsonProperty("personalize", Boolean, true),
    __metadata("design:type", Boolean)
], CrossSellingProduct.prototype, "personalize", undefined);
__decorate([
    JsonProperty("persoProductType", String, true),
    __metadata("design:type", String)
], CrossSellingProduct.prototype, "persoProductType", undefined);
CrossSellingProduct = __decorate([
    JsonObject("CrossSellingProduct")
], CrossSellingProduct);

/**
 * This interface is used to describe collection's items of CrossSelling
 */
let CrossSellingItem = class CrossSellingItem extends ModelObject {
    constructor() {
        super(...arguments);
        /** Total of Product in this collection of cross-sell */
        this.total = undefined;
        /** Array of cross-selling Product */
        this.items = undefined;
    }
};
__decorate([
    JsonProperty("total", Number, true),
    __metadata("design:type", Number)
], CrossSellingItem.prototype, "total", undefined);
__decorate([
    JsonProperty("items", [CrossSellingProduct], true),
    __metadata("design:type", Array)
], CrossSellingItem.prototype, "items", undefined);
CrossSellingItem = __decorate([
    JsonObject("CrossSellingItem")
], CrossSellingItem);

/**
 * This interface is used to describe data in response of CrossSelling
 */
let CrossSellingResponse = class CrossSellingResponse extends ModelObject {
    constructor() {
        super(...arguments);
        this.keepExploring = undefined;
        this.perfectPartner = undefined;
    }
};
__decorate([
    JsonProperty("keepExploring", CrossSellingItem, true),
    __metadata("design:type", CrossSellingItem)
], CrossSellingResponse.prototype, "keepExploring", undefined);
__decorate([
    JsonProperty("perfectPartner", CrossSellingItem, true),
    __metadata("design:type", CrossSellingItem)
], CrossSellingResponse.prototype, "perfectPartner", undefined);
CrossSellingResponse = __decorate([
    JsonObject("CrossSellingResponse")
], CrossSellingResponse);

/**
 * A simple key-value object to store attributes.
 * These attributes are only used for GiftSet Products
 */
let GiftSetProductAttributes = class GiftSetProductAttributes extends AbstractProductAttributes {
    constructor() {
        super(...arguments);
        this.slot = undefined;
        this.contenance = undefined;
        /** Sku of set in case the product is a set */
        this.skuSet = undefined;
    }
};
__decorate([
    JsonProperty("slot", Number, true),
    __metadata("design:type", Number)
], GiftSetProductAttributes.prototype, "slot", undefined);
__decorate([
    JsonProperty("contenance", String, true),
    __metadata("design:type", String)
], GiftSetProductAttributes.prototype, "contenance", undefined);
__decorate([
    JsonProperty("skuSet", String, true),
    __metadata("design:type", String)
], GiftSetProductAttributes.prototype, "skuSet", undefined);
GiftSetProductAttributes = __decorate([
    JsonObject("GiftSetProductAttributes")
], GiftSetProductAttributes);

/**
 * This class describe a product variant buckle
 */
let ProductBuckleVariant = class ProductBuckleVariant extends AbstractProductVariant {
    constructor() {
        super(...arguments);
        this.image = undefined;
        this.skuBuckle = undefined;
        this.color = undefined;
        this.title = undefined;
        this.displayNameList = undefined;
    }
};
__decorate([
    JsonProperty("image", String, true),
    __metadata("design:type", String)
], ProductBuckleVariant.prototype, "image", undefined);
__decorate([
    JsonProperty("skuBuckle", String, true),
    __metadata("design:type", String)
], ProductBuckleVariant.prototype, "skuBuckle", undefined);
__decorate([
    JsonProperty("color", String, true),
    __metadata("design:type", String)
], ProductBuckleVariant.prototype, "color", undefined);
__decorate([
    JsonProperty("title", String, true),
    __metadata("design:type", String)
], ProductBuckleVariant.prototype, "title", undefined);
__decorate([
    JsonProperty("displayNameList", String, true),
    __metadata("design:type", String)
], ProductBuckleVariant.prototype, "displayNameList", undefined);
ProductBuckleVariant = __decorate([
    JsonObject("ProductBuckleVariant")
], ProductBuckleVariant);

/**
 * This class describe a product variant color
 */
let ProductColorVariant = class ProductColorVariant extends AbstractProductVariant {
    constructor() {
        super(...arguments);
        this.url = undefined;
        this.image = undefined;
        this.color = undefined;
        this.refColoris = undefined;
        /** Only used for quick buy modal variants */
        this.price = undefined;
        /** Only used for quick buy modal variants */
        this.title = undefined;
        /** Only used for quick buy modal variants */
        this.displayNameList = undefined;
    }
};
__decorate([
    JsonProperty("url", String, true),
    __metadata("design:type", String)
], ProductColorVariant.prototype, "url", undefined);
__decorate([
    JsonProperty("image", String, true),
    __metadata("design:type", String)
], ProductColorVariant.prototype, "image", undefined);
__decorate([
    JsonProperty("color", String, true),
    __metadata("design:type", String)
], ProductColorVariant.prototype, "color", undefined);
__decorate([
    JsonProperty("refColoris", String, true),
    __metadata("design:type", String)
], ProductColorVariant.prototype, "refColoris", undefined);
__decorate([
    JsonProperty("price", Number, true),
    __metadata("design:type", Number)
], ProductColorVariant.prototype, "price", undefined);
__decorate([
    JsonProperty("title", String, true),
    __metadata("design:type", String)
], ProductColorVariant.prototype, "title", undefined);
__decorate([
    JsonProperty("display_name_list", String, true),
    __metadata("design:type", String)
], ProductColorVariant.prototype, "displayNameList", undefined);
ProductColorVariant = __decorate([
    JsonObject("ProductColorVariant")
], ProductColorVariant);

/**
 * Provides an edito for product page
 * Could be a simple html content or media of type image or video
 */
let ProductEdito = class ProductEdito extends ModelObject {
    constructor() {
        super(...arguments);
        this.title = undefined;
        this.content = undefined;
        this.contentTitle = undefined;
        this.desktopImage = undefined;
        this.mobileImage = undefined;
        this.imageAlt = undefined;
        this.imageRetouched = undefined;
        this.videoUrl = undefined;
        this.videoLoopMuteAutoplay = undefined;
        this.alwaysShowControls = undefined;
        this.videoPosterUrl = undefined;
        this.mediaType = undefined;
    }
};
__decorate([
    JsonProperty("title", String, true),
    __metadata("design:type", String)
], ProductEdito.prototype, "title", undefined);
__decorate([
    JsonProperty("content", String, true),
    __metadata("design:type", String)
], ProductEdito.prototype, "content", undefined);
__decorate([
    JsonProperty("contentTitle", String, true),
    __metadata("design:type", String)
], ProductEdito.prototype, "contentTitle", undefined);
__decorate([
    JsonProperty("desktopImage", String, true),
    __metadata("design:type", String)
], ProductEdito.prototype, "desktopImage", undefined);
__decorate([
    JsonProperty("mobileImage", String, true),
    __metadata("design:type", String)
], ProductEdito.prototype, "mobileImage", undefined);
__decorate([
    JsonProperty("imageAlt", String, true),
    __metadata("design:type", String)
], ProductEdito.prototype, "imageAlt", undefined);
__decorate([
    JsonProperty("imageRetouched", Boolean, true),
    __metadata("design:type", Boolean)
], ProductEdito.prototype, "imageRetouched", undefined);
__decorate([
    JsonProperty("videoUrl", String, true),
    __metadata("design:type", String)
], ProductEdito.prototype, "videoUrl", undefined);
__decorate([
    JsonProperty("videoLoopMuteAutoplay", Boolean, true),
    __metadata("design:type", Boolean)
], ProductEdito.prototype, "videoLoopMuteAutoplay", undefined);
__decorate([
    JsonProperty("alwaysShowControls", Boolean, true),
    __metadata("design:type", Boolean)
], ProductEdito.prototype, "alwaysShowControls", undefined);
__decorate([
    JsonProperty("videoPosterUrl", String, true),
    __metadata("design:type", String)
], ProductEdito.prototype, "videoPosterUrl", undefined);
__decorate([
    JsonProperty("mediaType", String, true),
    __metadata("design:type", String)
], ProductEdito.prototype, "mediaType", undefined);
ProductEdito = __decorate([
    JsonObject("ProductEdito")
], ProductEdito);

let ProductNeedHelpCallingHour = class ProductNeedHelpCallingHour {
    constructor() {
        this.closing_hours = undefined;
        this.days = undefined;
        this.opening_hours = undefined;
        this.timezone = undefined;
    }
};
__decorate([
    JsonProperty("closing_hours", String, true),
    __metadata("design:type", String)
], ProductNeedHelpCallingHour.prototype, "closing_hours", undefined);
__decorate([
    JsonProperty("days", String, true),
    __metadata("design:type", String)
], ProductNeedHelpCallingHour.prototype, "days", undefined);
__decorate([
    JsonProperty("opening_hours", String, true),
    __metadata("design:type", String)
], ProductNeedHelpCallingHour.prototype, "opening_hours", undefined);
__decorate([
    JsonProperty("timezone", String, true),
    __metadata("design:type", String)
], ProductNeedHelpCallingHour.prototype, "timezone", undefined);
ProductNeedHelpCallingHour = __decorate([
    JsonObject("ProductNeedHelpCallingHour")
], ProductNeedHelpCallingHour);

let ProductNeedHelpContactInfo = class ProductNeedHelpContactInfo {
    constructor() {
        this.phone = undefined;
        this.description = undefined;
        this.calling_hours = undefined;
    }
};
__decorate([
    JsonProperty("phone", String, true),
    __metadata("design:type", String)
], ProductNeedHelpContactInfo.prototype, "phone", undefined);
__decorate([
    JsonProperty("description", String, true),
    __metadata("design:type", String)
], ProductNeedHelpContactInfo.prototype, "description", undefined);
__decorate([
    JsonProperty("calling_hours", [ProductNeedHelpCallingHour], true),
    __metadata("design:type", Array)
], ProductNeedHelpContactInfo.prototype, "calling_hours", undefined);
ProductNeedHelpContactInfo = __decorate([
    JsonObject("ProductNeedHelpContactInfo")
], ProductNeedHelpContactInfo);

let ProductNeedHelp = class ProductNeedHelp {
    constructor() {
        this.title = undefined;
        this.sub_title = undefined;
        this.body = undefined;
        this.contact_infos = undefined;
        this.contact_title = undefined;
    }
};
__decorate([
    JsonProperty("title", String, true),
    __metadata("design:type", String)
], ProductNeedHelp.prototype, "title", undefined);
__decorate([
    JsonProperty("sub_title", String, true),
    __metadata("design:type", String)
], ProductNeedHelp.prototype, "sub_title", undefined);
__decorate([
    JsonProperty("body", String, true),
    __metadata("design:type", String)
], ProductNeedHelp.prototype, "body", undefined);
__decorate([
    JsonProperty("contact_infos", [ProductNeedHelpContactInfo], true),
    __metadata("design:type", Array)
], ProductNeedHelp.prototype, "contact_infos", undefined);
__decorate([
    JsonProperty("contact_title", String, true),
    __metadata("design:type", String)
], ProductNeedHelp.prototype, "contact_title", undefined);
ProductNeedHelp = __decorate([
    JsonObject("ProductNeedHelp")
], ProductNeedHelp);

/**
 * Rendered content displayed in product page toggles
 */
let ProductToggles = class ProductToggles {
    constructor() {
        // Editorial Content (Drupal)
        this.productDetail = undefined;
        this.productDetailTitle = undefined;
        this.gift = undefined;
        this.careInstructions = undefined;
        // PIM Attribute
        this.sellingConditions = undefined;
        this.deliveryAndReturns = undefined;
        this.moreInformation = undefined;
        this.moreInformationTitle = undefined;
        // BEAUTY Attributes
        this.applicationAdviceTitle = undefined;
        this.applicationAdvice = undefined;
        this.ingredientsTitle = undefined;
        this.ingredients = undefined;
    }
};
__decorate([
    JsonProperty("product_detail", String, true),
    __metadata("design:type", String)
], ProductToggles.prototype, "productDetail", undefined);
__decorate([
    JsonProperty("product_detail_title", String, true),
    __metadata("design:type", String)
], ProductToggles.prototype, "productDetailTitle", undefined);
__decorate([
    JsonProperty("gift", String, true),
    __metadata("design:type", String)
], ProductToggles.prototype, "gift", undefined);
__decorate([
    JsonProperty("care_instruction", String, true),
    __metadata("design:type", String)
], ProductToggles.prototype, "careInstructions", undefined);
__decorate([
    JsonProperty("selling_conditions", String, true),
    __metadata("design:type", String)
], ProductToggles.prototype, "sellingConditions", undefined);
__decorate([
    JsonProperty("delivery_and_returns", String, true),
    __metadata("design:type", String)
], ProductToggles.prototype, "deliveryAndReturns", undefined);
__decorate([
    JsonProperty("more_information", String, true),
    __metadata("design:type", String)
], ProductToggles.prototype, "moreInformation", undefined);
__decorate([
    JsonProperty("more_information_title", String, true),
    __metadata("design:type", String)
], ProductToggles.prototype, "moreInformationTitle", undefined);
__decorate([
    JsonProperty("application_advice_title", String, true),
    __metadata("design:type", String)
], ProductToggles.prototype, "applicationAdviceTitle", undefined);
__decorate([
    JsonProperty("application_advice", String, true),
    __metadata("design:type", String)
], ProductToggles.prototype, "applicationAdvice", undefined);
__decorate([
    JsonProperty("ingredients_title", String, true),
    __metadata("design:type", String)
], ProductToggles.prototype, "ingredientsTitle", undefined);
__decorate([
    JsonProperty("ingredients", String, true),
    __metadata("design:type", String)
], ProductToggles.prototype, "ingredients", undefined);
ProductToggles = __decorate([
    JsonObject("ProductToggles")
], ProductToggles);

let ProductContent = class ProductContent {
    constructor() {
        /**
         * Rendered content appearing in the product information toggles
         */
        this.toggles = undefined;
        this.storyBehind = undefined;
        this.sizeGuideId = undefined;
        this.needHelp = undefined;
    }
};
__decorate([
    JsonProperty("toggles", ProductToggles, true),
    __metadata("design:type", ProductToggles)
], ProductContent.prototype, "toggles", undefined);
__decorate([
    JsonProperty("story_behind", [ProductEdito], true),
    __metadata("design:type", Array)
], ProductContent.prototype, "storyBehind", undefined);
__decorate([
    JsonProperty("sizeGuideId", String, true),
    __metadata("design:type", String)
], ProductContent.prototype, "sizeGuideId", undefined);
__decorate([
    JsonProperty("need_help", ProductNeedHelp, true),
    __metadata("design:type", ProductNeedHelp)
], ProductContent.prototype, "needHelp", undefined);
ProductContent = __decorate([
    JsonObject("ProductContent")
], ProductContent);

/**
 * This class describe a product variant finish
 */
let ProductFinishVariant = class ProductFinishVariant extends AbstractProductVariant {
    constructor() {
        super(...arguments);
        this.image = undefined;
        this.itemSku = undefined;
        this.title = undefined;
        this.url = undefined;
        this.finish = undefined;
    }
};
__decorate([
    JsonProperty("image", String, true),
    __metadata("design:type", String)
], ProductFinishVariant.prototype, "image", undefined);
__decorate([
    JsonProperty("itemSku", String, true),
    __metadata("design:type", String)
], ProductFinishVariant.prototype, "itemSku", undefined);
__decorate([
    JsonProperty("title", String, true),
    __metadata("design:type", String)
], ProductFinishVariant.prototype, "title", undefined);
__decorate([
    JsonProperty("url", String, true),
    __metadata("design:type", String)
], ProductFinishVariant.prototype, "url", undefined);
__decorate([
    JsonProperty("finish", String, true),
    __metadata("design:type", String)
], ProductFinishVariant.prototype, "finish", undefined);
ProductFinishVariant = __decorate([
    JsonObject("ProductFinishVariant")
], ProductFinishVariant);

const LEAD_TIME_FORMAT = {
    FROM: "from",
    TO: "to",
    FROM_TO: "from_to",
    DATE: "date",
    DEFAULT: "default",
};
const LEAD_TIME_PERIOD = {
    DAYS: "days",
    WEEKS: "weeks",
    MONTHS: "months",
};
/**
 * Informations about the lead time data of a product.
 * @dynamic
 */
let ProductLeadTime = class ProductLeadTime extends ModelObject {
    constructor(source) {
        super();
        this.format = "default";
        this.shippingStartDate = undefined;
        this.to = undefined;
        this.from = undefined;
        this.period = undefined;
        Object.assign(this, source);
    }
};
__decorate([
    JsonProperty("format", String),
    __metadata("design:type", String)
], ProductLeadTime.prototype, "format", undefined);
__decorate([
    JsonProperty("shippingStartDate", String, true),
    __metadata("design:type", String)
], ProductLeadTime.prototype, "shippingStartDate", undefined);
__decorate([
    JsonProperty("to", Number, true),
    __metadata("design:type", Number)
], ProductLeadTime.prototype, "to", undefined);
__decorate([
    JsonProperty("from", Number, true),
    __metadata("design:type", Number)
], ProductLeadTime.prototype, "from", undefined);
__decorate([
    JsonProperty("period", String, true),
    __metadata("design:type", String)
], ProductLeadTime.prototype, "period", undefined);
ProductLeadTime = __decorate([
    JsonObject("ProductLeadTime"),
    __metadata("design:paramtypes", [Object])
], ProductLeadTime);

/**
 * This class describe a product giftset item
 */
let ProductGiftSetItem = class ProductGiftSetItem {
    constructor() {
        this.sku = undefined;
        this.title = undefined;
        this.displayNameList = undefined;
        this.url = undefined;
        this.image = undefined;
        this.ingredientsTitle = undefined;
        this.ingredients = undefined;
        this.descriptionTitle = undefined;
        this.description = undefined;
        this.price = undefined;
        this.unitPrice = undefined;
        this.unitPriceDesc = undefined;
        this.stock = undefined;
        this.departmentCode = undefined;
        this.divisionCode = undefined;
        this.productCode = undefined;
        this.familyCode = undefined;
        this.shippingInformation = undefined;
        this.leadTime = undefined;
        this.madeIn = undefined;
    }
};
__decorate([
    JsonProperty("sku", String, true),
    __metadata("design:type", String)
], ProductGiftSetItem.prototype, "sku", undefined);
__decorate([
    JsonProperty("title", String, true),
    __metadata("design:type", String)
], ProductGiftSetItem.prototype, "title", undefined);
__decorate([
    JsonProperty("displayNameList", String, true),
    __metadata("design:type", String)
], ProductGiftSetItem.prototype, "displayNameList", undefined);
__decorate([
    JsonProperty("url", String, true),
    __metadata("design:type", String)
], ProductGiftSetItem.prototype, "url", undefined);
__decorate([
    JsonProperty("image", String, true),
    __metadata("design:type", String)
], ProductGiftSetItem.prototype, "image", undefined);
__decorate([
    JsonProperty("ingredientsTitle", String, true),
    __metadata("design:type", String)
], ProductGiftSetItem.prototype, "ingredientsTitle", undefined);
__decorate([
    JsonProperty("ingredients", String, true),
    __metadata("design:type", String)
], ProductGiftSetItem.prototype, "ingredients", undefined);
__decorate([
    JsonProperty("descriptionTitle", String, true),
    __metadata("design:type", String)
], ProductGiftSetItem.prototype, "descriptionTitle", undefined);
__decorate([
    JsonProperty("description", String, true),
    __metadata("design:type", String)
], ProductGiftSetItem.prototype, "description", undefined);
__decorate([
    JsonProperty("price", String, true),
    __metadata("design:type", String)
], ProductGiftSetItem.prototype, "price", undefined);
__decorate([
    JsonProperty("unitPrice", String, true),
    __metadata("design:type", String)
], ProductGiftSetItem.prototype, "unitPrice", undefined);
__decorate([
    JsonProperty("unitPriceDesc", String, true),
    __metadata("design:type", String)
], ProductGiftSetItem.prototype, "unitPriceDesc", undefined);
__decorate([
    JsonProperty("stock", ProductStock, true),
    __metadata("design:type", ProductStock)
], ProductGiftSetItem.prototype, "stock", undefined);
__decorate([
    JsonProperty("departmentCode", String, true),
    __metadata("design:type", String)
], ProductGiftSetItem.prototype, "departmentCode", undefined);
__decorate([
    JsonProperty("divisionCode", String, true),
    __metadata("design:type", String)
], ProductGiftSetItem.prototype, "divisionCode", undefined);
__decorate([
    JsonProperty("productCode", String, true),
    __metadata("design:type", String)
], ProductGiftSetItem.prototype, "productCode", undefined);
__decorate([
    JsonProperty("familyCode", String, true),
    __metadata("design:type", String)
], ProductGiftSetItem.prototype, "familyCode", undefined);
__decorate([
    JsonProperty("shippingInformation", String, true),
    __metadata("design:type", String)
], ProductGiftSetItem.prototype, "shippingInformation", undefined);
__decorate([
    JsonProperty("leadTime", ProductLeadTime, true),
    __metadata("design:type", ProductLeadTime)
], ProductGiftSetItem.prototype, "leadTime", undefined);
__decorate([
    JsonProperty("madeIn", String, true),
    __metadata("design:type", String)
], ProductGiftSetItem.prototype, "madeIn", undefined);
ProductGiftSetItem = __decorate([
    JsonObject("ProductGiftSetItem")
], ProductGiftSetItem);

let AgecLink = class AgecLink {
    constructor() {
        this.sku = undefined;
        this.isAgec = undefined;
        this.objectTypeTitle = undefined;
    }
};
__decorate([
    JsonProperty("sku", String, false),
    __metadata("design:type", String)
], AgecLink.prototype, "sku", undefined);
__decorate([
    JsonProperty("isAgec", Boolean, false),
    __metadata("design:type", Boolean)
], AgecLink.prototype, "isAgec", undefined);
__decorate([
    JsonProperty("objectTypeTitle", String, true),
    __metadata("design:type", String)
], AgecLink.prototype, "objectTypeTitle", undefined);
AgecLink = __decorate([
    JsonObject("AgecLink")
], AgecLink);

/**
 * This class describe a product variant leather
 */
let ProductLeatherVariant = class ProductLeatherVariant extends AbstractProductVariant {
    constructor() {
        super(...arguments);
        this.color = undefined;
        this.skuLeather = undefined;
        this.image = undefined;
        this.title = undefined;
        this.refColoris = undefined;
    }
};
__decorate([
    JsonProperty("color", String, true),
    __metadata("design:type", String)
], ProductLeatherVariant.prototype, "color", undefined);
__decorate([
    JsonProperty("skuLeather", String, true),
    __metadata("design:type", String)
], ProductLeatherVariant.prototype, "skuLeather", undefined);
__decorate([
    JsonProperty("image", String, true),
    __metadata("design:type", String)
], ProductLeatherVariant.prototype, "image", undefined);
__decorate([
    JsonProperty("title", String, true),
    __metadata("design:type", String)
], ProductLeatherVariant.prototype, "title", undefined);
__decorate([
    JsonProperty("refColoris", String, true),
    __metadata("design:type", String)
], ProductLeatherVariant.prototype, "refColoris", undefined);
ProductLeatherVariant = __decorate([
    JsonObject("ProductLeatherVariant")
], ProductLeatherVariant);

/**
 * This class describe a product variant size
 */
let ProductSizeVariant = class ProductSizeVariant extends AbstractProductVariant {
    constructor() {
        super(...arguments);
        this.size = undefined;
        /** Only used for look items variants */
        this.shippingInformation = undefined;
        /** Only used for look and bikini items variants */
        this.leadTime = undefined;
        /** Only used for look items variants */
        this.refSizeCode = undefined;
        /** Only used for watch sizes variants */
        this.modelSize = undefined;
        /** Only used for watch strap length variants */
        this.refDimensions = undefined;
        /** Only used for quick buy modal variants */
        this.price = undefined;
        /** Only used for quick buy modal variants */
        this.title = undefined;
        /** Only used for quick buy modal variants */
        this.displayNameList = undefined;
        /** Only used for quick buy modal variants */
        this.color = undefined;
    }
};
__decorate([
    JsonProperty("size", String, true),
    __metadata("design:type", String)
], ProductSizeVariant.prototype, "size", undefined);
__decorate([
    JsonProperty("shipping_information", String, true),
    __metadata("design:type", String)
], ProductSizeVariant.prototype, "shippingInformation", undefined);
__decorate([
    JsonProperty("leadTime", ProductLeadTime, true),
    __metadata("design:type", ProductLeadTime)
], ProductSizeVariant.prototype, "leadTime", undefined);
__decorate([
    JsonProperty("ref_size_code", String, true),
    __metadata("design:type", String)
], ProductSizeVariant.prototype, "refSizeCode", undefined);
__decorate([
    JsonProperty("model_size", String, true),
    __metadata("design:type", String)
], ProductSizeVariant.prototype, "modelSize", undefined);
__decorate([
    JsonProperty("ref_dimensions", String, true),
    __metadata("design:type", String)
], ProductSizeVariant.prototype, "refDimensions", undefined);
__decorate([
    JsonProperty("price", Number, true),
    __metadata("design:type", Number)
], ProductSizeVariant.prototype, "price", undefined);
__decorate([
    JsonProperty("title", String, true),
    __metadata("design:type", String)
], ProductSizeVariant.prototype, "title", undefined);
__decorate([
    JsonProperty("display_name_list", String, true),
    __metadata("design:type", String)
], ProductSizeVariant.prototype, "displayNameList", undefined);
__decorate([
    JsonProperty("color", String, true),
    __metadata("design:type", String)
], ProductSizeVariant.prototype, "color", undefined);
ProductSizeVariant = __decorate([
    JsonObject("ProductSizeVariant")
], ProductSizeVariant);

/**
 * This class describe a product variant strap color
 */
let ProductWatchStrapVariant = class ProductWatchStrapVariant extends ProductColorVariant {
    constructor() {
        super(...arguments);
        this.nbTours = undefined;
    }
};
__decorate([
    JsonProperty("nbTours", String, true),
    __metadata("design:type", String)
], ProductWatchStrapVariant.prototype, "nbTours", undefined);
ProductWatchStrapVariant = __decorate([
    JsonObject("ProductWatchStrapVariant")
], ProductWatchStrapVariant);

/**
 * This class describe a product variant leather
 */
let ProductStrapVariant = class ProductStrapVariant extends AbstractProductVariant {
    constructor() {
        super(...arguments);
        this.color = undefined;
        this.image = undefined;
        this.itemSku = undefined;
        this.title = undefined;
        this.refColoris = undefined;
        this.url = undefined;
    }
};
__decorate([
    JsonProperty("color", String, true),
    __metadata("design:type", String)
], ProductStrapVariant.prototype, "color", undefined);
__decorate([
    JsonProperty("image", String, true),
    __metadata("design:type", String)
], ProductStrapVariant.prototype, "image", undefined);
__decorate([
    JsonProperty("itemSku", String, true),
    __metadata("design:type", String)
], ProductStrapVariant.prototype, "itemSku", undefined);
__decorate([
    JsonProperty("title", String, true),
    __metadata("design:type", String)
], ProductStrapVariant.prototype, "title", undefined);
__decorate([
    JsonProperty("refColoris", String, true),
    __metadata("design:type", String)
], ProductStrapVariant.prototype, "refColoris", undefined);
__decorate([
    JsonProperty("url", String, true),
    __metadata("design:type", String)
], ProductStrapVariant.prototype, "url", undefined);
ProductStrapVariant = __decorate([
    JsonObject("ProductStrapVariant")
], ProductStrapVariant);

/**
 * This class describe a product variant of type volume
 */
let ProductVolumeVariant = class ProductVolumeVariant extends AbstractProductVariant {
    constructor() {
        super(...arguments);
        this.volume = undefined;
        /** Only used for quick buy modal variants */
        this.price = undefined;
        /** Only used for quick buy modal variants */
        this.title = undefined;
        /** Only used for quick buy modal variants */
        this.displayNameList = undefined;
        /** Only used for quick buy modal variants */
        this.measureUnit = undefined;
    }
};
__decorate([
    JsonProperty("volume", String, true),
    __metadata("design:type", String)
], ProductVolumeVariant.prototype, "volume", undefined);
__decorate([
    JsonProperty("price", Number, true),
    __metadata("design:type", Number)
], ProductVolumeVariant.prototype, "price", undefined);
__decorate([
    JsonProperty("title", String, true),
    __metadata("design:type", String)
], ProductVolumeVariant.prototype, "title", undefined);
__decorate([
    JsonProperty("display_name_list", String, true),
    __metadata("design:type", String)
], ProductVolumeVariant.prototype, "displayNameList", undefined);
__decorate([
    JsonProperty("measure_unit", String, true),
    __metadata("design:type", String)
], ProductVolumeVariant.prototype, "measureUnit", undefined);
ProductVolumeVariant = __decorate([
    JsonObject("ProductVolumeVariant")
], ProductVolumeVariant);

/**
 * This class describe the product variants of a Product
 */
let ProductVariants = class ProductVariants {
    constructor() {
        /**
         * Color variations.
         */
        this.colors = undefined;
        /**
         * Size variations.
         */
        this.sizes = undefined;
        /**
         * Volume variations.
         */
        this.volumes = undefined;
        /**
         * Buckle variations.
         */
        this.buckles = undefined;
        /**
         * Leather variations.
         */
        this.leathers = undefined;
        /**
         * Strap variations.
         */
        this.straps = undefined;
        /**
         * Apple Watch strap size variations.
         */
        this.strapSizes = undefined;
        /**
         * Leather variations.
         */
        this.finishes = undefined;
        /**
         * Strap colors variations.
         */
        this.strapColors = undefined;
        /**
         * Watch sizes variations.
         */
        this.watchSizes = undefined;
        /**
         * Strap lengths variations.
         */
        this.strapLengths = undefined;
    }
};
__decorate([
    JsonProperty("colors", [ProductColorVariant], true),
    __metadata("design:type", Array)
], ProductVariants.prototype, "colors", undefined);
__decorate([
    JsonProperty("sizes", [ProductSizeVariant], true),
    __metadata("design:type", Array)
], ProductVariants.prototype, "sizes", undefined);
__decorate([
    JsonProperty("volumes", [ProductVolumeVariant], true),
    __metadata("design:type", Array)
], ProductVariants.prototype, "volumes", undefined);
__decorate([
    JsonProperty("buckles", [ProductBuckleVariant], true),
    __metadata("design:type", Array)
], ProductVariants.prototype, "buckles", undefined);
__decorate([
    JsonProperty("leathers", [ProductLeatherVariant], true),
    __metadata("design:type", Array)
], ProductVariants.prototype, "leathers", undefined);
__decorate([
    JsonProperty("straps", [ProductStrapVariant], true),
    __metadata("design:type", Array)
], ProductVariants.prototype, "straps", undefined);
__decorate([
    JsonProperty("strapSizes", [ProductSizeVariant], true),
    __metadata("design:type", Array)
], ProductVariants.prototype, "strapSizes", undefined);
__decorate([
    JsonProperty("finishes", [ProductFinishVariant], true),
    __metadata("design:type", Array)
], ProductVariants.prototype, "finishes", undefined);
__decorate([
    JsonProperty("strap_colors", [ProductWatchStrapVariant], true),
    __metadata("design:type", Array)
], ProductVariants.prototype, "strapColors", undefined);
__decorate([
    JsonProperty("watch_sizes", [ProductSizeVariant], true),
    __metadata("design:type", Array)
], ProductVariants.prototype, "watchSizes", undefined);
__decorate([
    JsonProperty("strap_lengths", [ProductSizeVariant], true),
    __metadata("design:type", Array)
], ProductVariants.prototype, "strapLengths", undefined);
ProductVariants = __decorate([
    JsonObject("ProductVariants")
], ProductVariants);

/**
 * Class of a product item in a Bundle / Bikini template page
 */
let ProductItemBundleBikini = class ProductItemBundleBikini {
    constructor() {
        /** Product's title */
        this.title = undefined;
        this.sku = undefined;
        /** Description's title of the product */
        this.descriptionTitle = undefined;
        /** Product's description */
        this.descriptionContent = undefined;
        /** Title of the 'details' section inside the product's toggle */
        this.productDetailsTitle = undefined;
        /** Content of the 'details' section inside the product's toggle */
        this.productDetails = undefined;
        /** Content of the 'care instructions' section inside the product's toggle */
        this.careInstruction = undefined;
        this.sizeGuideId = undefined;
        /** Label of the color displayed in the 'color' section of the product */
        this.colorHermesLabel = undefined;
        /** Code of the color displayed in the 'color' section of the product */
        this.colorHermes = undefined;
        /** Label of the size attributes displayed in the 'size' section of the product */
        this.refSizeLabel = undefined;
        /** Product's Item department code */
        this.departmentCode = undefined;
        /** Product's Item family code */
        this.familyCode = undefined;
        /** Product's Item product code */
        this.productCode = undefined;
        /** Product's Item division code */
        this.divisionCode = undefined;
        /** List of size variants of a Bundle / Bikini Product */
        this.variants = undefined;
        /** Stock of the product and it's variants */
        this.stock = undefined;
        /** Array of assets */
        this.assets = [];
        this.madeIn = undefined;
        this.objectTypeTitle = undefined;
    }
};
__decorate([
    JsonProperty("title", String, false),
    __metadata("design:type", String)
], ProductItemBundleBikini.prototype, "title", undefined);
__decorate([
    JsonProperty("sku", String, true),
    __metadata("design:type", String)
], ProductItemBundleBikini.prototype, "sku", undefined);
__decorate([
    JsonProperty("descriptionTitle", String, true),
    __metadata("design:type", String)
], ProductItemBundleBikini.prototype, "descriptionTitle", undefined);
__decorate([
    JsonProperty("descriptionContent", String, true),
    __metadata("design:type", String)
], ProductItemBundleBikini.prototype, "descriptionContent", undefined);
__decorate([
    JsonProperty("productDetailsTitle", String, true),
    __metadata("design:type", String)
], ProductItemBundleBikini.prototype, "productDetailsTitle", undefined);
__decorate([
    JsonProperty("productDetails", String, true),
    __metadata("design:type", String)
], ProductItemBundleBikini.prototype, "productDetails", undefined);
__decorate([
    JsonProperty("careInstruction", String, true),
    __metadata("design:type", String)
], ProductItemBundleBikini.prototype, "careInstruction", undefined);
__decorate([
    JsonProperty("sizeGuideId", String, true),
    __metadata("design:type", String)
], ProductItemBundleBikini.prototype, "sizeGuideId", undefined);
__decorate([
    JsonProperty("colorHermesLabel", String, true),
    __metadata("design:type", String)
], ProductItemBundleBikini.prototype, "colorHermesLabel", undefined);
__decorate([
    JsonProperty("colorHermes", String, true),
    __metadata("design:type", String)
], ProductItemBundleBikini.prototype, "colorHermes", undefined);
__decorate([
    JsonProperty("refSizeLabel", String, true),
    __metadata("design:type", String)
], ProductItemBundleBikini.prototype, "refSizeLabel", undefined);
__decorate([
    JsonProperty("departmentCode", String, true),
    __metadata("design:type", String)
], ProductItemBundleBikini.prototype, "departmentCode", undefined);
__decorate([
    JsonProperty("familyCode", String, true),
    __metadata("design:type", String)
], ProductItemBundleBikini.prototype, "familyCode", undefined);
__decorate([
    JsonProperty("productCode", String, true),
    __metadata("design:type", String)
], ProductItemBundleBikini.prototype, "productCode", undefined);
__decorate([
    JsonProperty("divisionCode", String, true),
    __metadata("design:type", String)
], ProductItemBundleBikini.prototype, "divisionCode", undefined);
__decorate([
    JsonProperty("variants", ProductVariants, true),
    __metadata("design:type", ProductVariants)
], ProductItemBundleBikini.prototype, "variants", undefined);
__decorate([
    JsonProperty("stock", ProductStock, true),
    __metadata("design:type", ProductStock)
], ProductItemBundleBikini.prototype, "stock", undefined);
__decorate([
    JsonProperty("assets", [Asset], true),
    __metadata("design:type", Array)
], ProductItemBundleBikini.prototype, "assets", undefined);
__decorate([
    JsonProperty("madeIn", String, true),
    __metadata("design:type", String)
], ProductItemBundleBikini.prototype, "madeIn", undefined);
__decorate([
    JsonProperty("objectTypeTitle", String, true),
    __metadata("design:type", String)
], ProductItemBundleBikini.prototype, "objectTypeTitle", undefined);
ProductItemBundleBikini = __decorate([
    JsonObject("ProductItemBundleBikini")
], ProductItemBundleBikini);

/**
 * Bikini attributes, list of ProductItemBundleBikini
 */
let BikiniAttributes = class BikiniAttributes extends AbstractProductAttributes {
    constructor() {
        super(...arguments);
        /** Product's Item */
        this.items = [];
    }
};
__decorate([
    JsonProperty("items", [ProductItemBundleBikini], true),
    __metadata("design:type", Array)
], BikiniAttributes.prototype, "items", undefined);
BikiniAttributes = __decorate([
    JsonObject("BikiniAttributes")
], BikiniAttributes);

/**
 * Class of a product item in a Bundle template page
 */
let ProductItemBundle = class ProductItemBundle extends ProductItemBundleBikini {
    constructor() {
        super(...arguments);
        this.applicationAdvice = undefined;
        this.applicationAdviceTitle = undefined;
        this.ingredients = undefined;
        this.ingredientsTitle = undefined;
        this.displayNameList = undefined;
    }
};
__decorate([
    JsonProperty("applicationAdvice", String, true),
    __metadata("design:type", String)
], ProductItemBundle.prototype, "applicationAdvice", undefined);
__decorate([
    JsonProperty("applicationAdviceTitle", String, true),
    __metadata("design:type", String)
], ProductItemBundle.prototype, "applicationAdviceTitle", undefined);
__decorate([
    JsonProperty("ingredients", String, true),
    __metadata("design:type", String)
], ProductItemBundle.prototype, "ingredients", undefined);
__decorate([
    JsonProperty("ingredientsTitle", String, true),
    __metadata("design:type", String)
], ProductItemBundle.prototype, "ingredientsTitle", undefined);
__decorate([
    JsonProperty("displayNameList", String, true),
    __metadata("design:type", String)
], ProductItemBundle.prototype, "displayNameList", undefined);
ProductItemBundle = __decorate([
    JsonObject("ProductItemBundle")
], ProductItemBundle);

/**
 * Bundle attributes, list of ProductItemBundle
 */
let BundleAttributes = class BundleAttributes extends AbstractProductAttributes {
    constructor() {
        super(...arguments);
        /** Product's Item */
        this.items = [];
    }
};
__decorate([
    JsonProperty("items", [ProductItemBundle], true),
    __metadata("design:type", Array)
], BundleAttributes.prototype, "items", undefined);
BundleAttributes = __decorate([
    JsonObject("BundleAttributes")
], BundleAttributes);

/**
 * A simple key-value object to store attributes.
 * These attributes are only used for Double Fragrances Products
 */
let DoubleFragranceAttributes = class DoubleFragranceAttributes extends AbstractProductAttributes {
    constructor() {
        super(...arguments);
        this.measureUnit = undefined;
        this.skuPerfume = undefined;
        this.skuAccessory = undefined;
        this.skuSet = undefined;
        this.departmentCodePerfume = undefined;
        this.departmentCodeAccessory = undefined;
        this.productCodePerfume = undefined;
        this.productCodeAccessory = undefined;
        this.familyCodePerfume = undefined;
        this.familyCodeAccessory = undefined;
        this.divisionCodePerfume = undefined;
        this.divisionCodeAccessory = undefined;
        this.madeIn = undefined;
        this.objectTypeTitlePerfume = undefined;
        this.madeInPerfume = undefined;
        this.objectTypeTitleAccessory = undefined;
        this.madeInAccessory = undefined;
    }
};
__decorate([
    JsonProperty("measureUnit", String, true),
    __metadata("design:type", String)
], DoubleFragranceAttributes.prototype, "measureUnit", undefined);
__decorate([
    JsonProperty("skuPerfume", String, true),
    __metadata("design:type", String)
], DoubleFragranceAttributes.prototype, "skuPerfume", undefined);
__decorate([
    JsonProperty("skuAccessory", String, true),
    __metadata("design:type", String)
], DoubleFragranceAttributes.prototype, "skuAccessory", undefined);
__decorate([
    JsonProperty("skuSet", String, true),
    __metadata("design:type", String)
], DoubleFragranceAttributes.prototype, "skuSet", undefined);
__decorate([
    JsonProperty("departmentCodePerfume", String, true),
    __metadata("design:type", String)
], DoubleFragranceAttributes.prototype, "departmentCodePerfume", undefined);
__decorate([
    JsonProperty("departmentCodeAccessory", String, true),
    __metadata("design:type", String)
], DoubleFragranceAttributes.prototype, "departmentCodeAccessory", undefined);
__decorate([
    JsonProperty("productCodePerfume", String, true),
    __metadata("design:type", String)
], DoubleFragranceAttributes.prototype, "productCodePerfume", undefined);
__decorate([
    JsonProperty("productCodeAccessory", String, true),
    __metadata("design:type", String)
], DoubleFragranceAttributes.prototype, "productCodeAccessory", undefined);
__decorate([
    JsonProperty("familyCodePerfume", String, true),
    __metadata("design:type", String)
], DoubleFragranceAttributes.prototype, "familyCodePerfume", undefined);
__decorate([
    JsonProperty("familyCodeAccessory", String, true),
    __metadata("design:type", String)
], DoubleFragranceAttributes.prototype, "familyCodeAccessory", undefined);
__decorate([
    JsonProperty("divisionCodePerfume", String, true),
    __metadata("design:type", String)
], DoubleFragranceAttributes.prototype, "divisionCodePerfume", undefined);
__decorate([
    JsonProperty("divisionCodeAccessory", String, true),
    __metadata("design:type", String)
], DoubleFragranceAttributes.prototype, "divisionCodeAccessory", undefined);
__decorate([
    JsonProperty("madeIn", String, true),
    __metadata("design:type", String)
], DoubleFragranceAttributes.prototype, "madeIn", undefined);
__decorate([
    JsonProperty("objectTypeTitlePerfume", String, true),
    __metadata("design:type", String)
], DoubleFragranceAttributes.prototype, "objectTypeTitlePerfume", undefined);
__decorate([
    JsonProperty("madeInPerfume", String, true),
    __metadata("design:type", String)
], DoubleFragranceAttributes.prototype, "madeInPerfume", undefined);
__decorate([
    JsonProperty("objectTypeTitleAccessory", String, true),
    __metadata("design:type", String)
], DoubleFragranceAttributes.prototype, "objectTypeTitleAccessory", undefined);
__decorate([
    JsonProperty("madeInAccessory", String, true),
    __metadata("design:type", String)
], DoubleFragranceAttributes.prototype, "madeInAccessory", undefined);
DoubleFragranceAttributes = __decorate([
    JsonObject("DoubleFragranceAttributes")
], DoubleFragranceAttributes);

/**
 * Class of a product item in a look template page
 */
let ProductItemLook = class ProductItemLook {
    constructor() {
        /** Product's title */
        this.title = undefined;
        this.sku = undefined;
        /** Description's title of the product */
        this.descriptionTitle = undefined;
        /** Product's description */
        this.descriptionContent = undefined;
        /** Product's price */
        this.price = undefined;
        /** Title of the 'details' section inside the product's toggle */
        this.productDetailsTitle = undefined;
        /** Content of the 'details' section inside the product's toggle */
        this.productDetails = undefined;
        /** Content of the 'care instructions' section inside the product's toggle */
        this.careInstruction = undefined;
        this.sizeGuideId = undefined;
        /** Label of the color displayed in the 'color' section of the product */
        this.colorHermesLabel = undefined;
        /** Code of the color displayed in the 'color' section of the product */
        this.colorHermes = undefined;
        /** Label of the size attributes displayed in the 'size' section of the product */
        this.refSizeLabel = undefined;
        /** Product's Item department code */
        this.departmentCode = undefined;
        /** Product's Item family code */
        this.familyCode = undefined;
        /** Product's Item product code */
        this.productCode = undefined;
        /** Product's Item division code */
        this.divisionCode = undefined;
        /** List of size variants of a Look Product */
        this.variants = undefined;
        /** Stock of the product and it's variants */
        this.stock = undefined;
        /** Array of assets */
        this.assets = [];
        this.madeIn = undefined;
    }
};
__decorate([
    JsonProperty("title", String, false),
    __metadata("design:type", String)
], ProductItemLook.prototype, "title", undefined);
__decorate([
    JsonProperty("sku", String, true),
    __metadata("design:type", String)
], ProductItemLook.prototype, "sku", undefined);
__decorate([
    JsonProperty("descriptionTitle", String, true),
    __metadata("design:type", String)
], ProductItemLook.prototype, "descriptionTitle", undefined);
__decorate([
    JsonProperty("descriptionContent", String, true),
    __metadata("design:type", String)
], ProductItemLook.prototype, "descriptionContent", undefined);
__decorate([
    JsonProperty("price", Number, true),
    __metadata("design:type", Number)
], ProductItemLook.prototype, "price", undefined);
__decorate([
    JsonProperty("productDetailsTitle", String, true),
    __metadata("design:type", String)
], ProductItemLook.prototype, "productDetailsTitle", undefined);
__decorate([
    JsonProperty("productDetails", String, true),
    __metadata("design:type", String)
], ProductItemLook.prototype, "productDetails", undefined);
__decorate([
    JsonProperty("careInstruction", String, true),
    __metadata("design:type", String)
], ProductItemLook.prototype, "careInstruction", undefined);
__decorate([
    JsonProperty("sizeGuideId", String, true),
    __metadata("design:type", String)
], ProductItemLook.prototype, "sizeGuideId", undefined);
__decorate([
    JsonProperty("colorHermesLabel", String, true),
    __metadata("design:type", String)
], ProductItemLook.prototype, "colorHermesLabel", undefined);
__decorate([
    JsonProperty("colorHermes", String, true),
    __metadata("design:type", String)
], ProductItemLook.prototype, "colorHermes", undefined);
__decorate([
    JsonProperty("refSizeLabel", String, true),
    __metadata("design:type", String)
], ProductItemLook.prototype, "refSizeLabel", undefined);
__decorate([
    JsonProperty("departmentCode", String, true),
    __metadata("design:type", String)
], ProductItemLook.prototype, "departmentCode", undefined);
__decorate([
    JsonProperty("familyCode", String, true),
    __metadata("design:type", String)
], ProductItemLook.prototype, "familyCode", undefined);
__decorate([
    JsonProperty("productCode", String, true),
    __metadata("design:type", String)
], ProductItemLook.prototype, "productCode", undefined);
__decorate([
    JsonProperty("divisionCode", String, true),
    __metadata("design:type", String)
], ProductItemLook.prototype, "divisionCode", undefined);
__decorate([
    JsonProperty("variants", ProductVariants, true),
    __metadata("design:type", ProductVariants)
], ProductItemLook.prototype, "variants", undefined);
__decorate([
    JsonProperty("stock", ProductStock, true),
    __metadata("design:type", ProductStock)
], ProductItemLook.prototype, "stock", undefined);
__decorate([
    JsonProperty("assets", [Asset], true),
    __metadata("design:type", Array)
], ProductItemLook.prototype, "assets", undefined);
__decorate([
    JsonProperty("madeIn", String, true),
    __metadata("design:type", String)
], ProductItemLook.prototype, "madeIn", undefined);
ProductItemLook = __decorate([
    JsonObject("ProductItemLook")
], ProductItemLook);

/**
 * Look attributes, list of productItemLook
 */
let LookAttributes = class LookAttributes extends AbstractProductAttributes {
    constructor() {
        super(...arguments);
        /** Product's Item */
        this.items = [];
    }
};
__decorate([
    JsonProperty("items", [ProductItemLook], true),
    __metadata("design:type", Array)
], LookAttributes.prototype, "items", undefined);
LookAttributes = __decorate([
    JsonObject("LookAttributes")
], LookAttributes);

/**
 * Informations about breadcrumb.
 */
let ProductBreadcrumb = class ProductBreadcrumb {
    constructor() {
        this.code = undefined;
        this.value = undefined;
        this.url = undefined;
    }
};
__decorate([
    JsonProperty("code", String, true),
    __metadata("design:type", String)
], ProductBreadcrumb.prototype, "code", undefined);
__decorate([
    JsonProperty("value", String, true),
    __metadata("design:type", String)
], ProductBreadcrumb.prototype, "value", undefined);
__decorate([
    JsonProperty("url", String, true),
    __metadata("design:type", String)
], ProductBreadcrumb.prototype, "url", undefined);
ProductBreadcrumb = __decorate([
    JsonObject("ProductBreadcrumb")
], ProductBreadcrumb);

/**
 * Abstract class of product attributes
 */
let ProductItem = class ProductItem extends ModelObject {
    constructor() {
        super();
        /** Product's SKU */
        this.sku = undefined;
        /** Product's title */
        this.title = undefined;
        /** Product's Code */
        this.productCode = undefined;
        /** Product's personalization type */
        this.persoProductType = undefined;
        /** Array of assets */
        this.assets = [];
    }
};
__decorate([
    JsonProperty("sku", String, true),
    __metadata("design:type", String)
], ProductItem.prototype, "sku", undefined);
__decorate([
    JsonProperty("title", String, true),
    __metadata("design:type", String)
], ProductItem.prototype, "title", undefined);
__decorate([
    JsonProperty("product_code", String, true),
    __metadata("design:type", String)
], ProductItem.prototype, "productCode", undefined);
__decorate([
    JsonProperty("perso_product_type", String, true),
    __metadata("design:type", String)
], ProductItem.prototype, "persoProductType", undefined);
__decorate([
    JsonProperty("assets", [Asset], true),
    __metadata("design:type", Array)
], ProductItem.prototype, "assets", undefined);
ProductItem = __decorate([
    JsonObject("ProductItem"),
    __metadata("design:paramtypes", [])
], ProductItem);

/**
 * Informations about the delivery time of a product.
 */
let ShippingDelay = class ShippingDelay {
    constructor() {
        this.min = 0;
        this.max = 0;
    }
};
__decorate([
    JsonProperty("min", Number),
    __metadata("design:type", Number)
], ShippingDelay.prototype, "min", undefined);
__decorate([
    JsonProperty("max", Number),
    __metadata("design:type", Number)
], ShippingDelay.prototype, "max", undefined);
ShippingDelay = __decorate([
    JsonObject("ShippingDelay")
], ShippingDelay);

/**
 * A simple key-value object to store attributes.
 * These attributes are only used for Simple Products
 */
let SimpleProductAttributes = class SimpleProductAttributes extends AbstractProductAttributes {
    constructor() {
        super(...arguments);
        /** Product mass & volume Label (for Fragrance & soap) */
        this.massVolumeLabel = undefined;
        this.measureUnit = undefined;
        this.refColoris = undefined;
        this.modelSize = undefined;
        this.wristCircumferenceLabel = undefined;
        this.wristCircumferenceValue = undefined;
        this.strapLabel = undefined;
        this.strapValue = undefined;
        this.nbTours = undefined;
        this.metaTitle = undefined;
        this.metaDescription = undefined;
        this.madeIn = undefined;
        this.objectTypeTitle = undefined;
        this.engravingOptions = undefined;
        this.finish = undefined;
        this.finishCode = undefined;
        this.finishLabel = undefined;
        this.specificTermsOfProductUse = undefined;
    }
};
__decorate([
    JsonProperty("massVolumeLabel", String, true),
    __metadata("design:type", String)
], SimpleProductAttributes.prototype, "massVolumeLabel", undefined);
__decorate([
    JsonProperty("measureUnit", String, true),
    __metadata("design:type", String)
], SimpleProductAttributes.prototype, "measureUnit", undefined);
__decorate([
    JsonProperty("refColoris", String, true),
    __metadata("design:type", String)
], SimpleProductAttributes.prototype, "refColoris", undefined);
__decorate([
    JsonProperty("modelSize", String, true),
    __metadata("design:type", String)
], SimpleProductAttributes.prototype, "modelSize", undefined);
__decorate([
    JsonProperty("wristCircumferenceLabel", String, true),
    __metadata("design:type", String)
], SimpleProductAttributes.prototype, "wristCircumferenceLabel", undefined);
__decorate([
    JsonProperty("wristCircumferenceValue", String, true),
    __metadata("design:type", String)
], SimpleProductAttributes.prototype, "wristCircumferenceValue", undefined);
__decorate([
    JsonProperty("strapLabel", String, true),
    __metadata("design:type", String)
], SimpleProductAttributes.prototype, "strapLabel", undefined);
__decorate([
    JsonProperty("strapValue", String, true),
    __metadata("design:type", String)
], SimpleProductAttributes.prototype, "strapValue", undefined);
__decorate([
    JsonProperty("nbTours", String, true),
    __metadata("design:type", String)
], SimpleProductAttributes.prototype, "nbTours", undefined);
__decorate([
    JsonProperty("metaTitle", String, true),
    __metadata("design:type", String)
], SimpleProductAttributes.prototype, "metaTitle", undefined);
__decorate([
    JsonProperty("metaDescription", String, true),
    __metadata("design:type", String)
], SimpleProductAttributes.prototype, "metaDescription", undefined);
__decorate([
    JsonProperty("madeIn", String, true),
    __metadata("design:type", String)
], SimpleProductAttributes.prototype, "madeIn", undefined);
__decorate([
    JsonProperty("objectTypeTitle", String, true),
    __metadata("design:type", String)
], SimpleProductAttributes.prototype, "objectTypeTitle", undefined);
__decorate([
    JsonProperty("engravingOptions", String, true),
    __metadata("design:type", String)
], SimpleProductAttributes.prototype, "engravingOptions", undefined);
__decorate([
    JsonProperty("finish", String, true),
    __metadata("design:type", String)
], SimpleProductAttributes.prototype, "finish", undefined);
__decorate([
    JsonProperty("finishCode", String, true),
    __metadata("design:type", String)
], SimpleProductAttributes.prototype, "finishCode", undefined);
__decorate([
    JsonProperty("finishLabel", String, true),
    __metadata("design:type", String)
], SimpleProductAttributes.prototype, "finishLabel", undefined);
__decorate([
    JsonProperty("specificTermsOfProductUse", Boolean, true),
    __metadata("design:type", Boolean)
], SimpleProductAttributes.prototype, "specificTermsOfProductUse", undefined);
SimpleProductAttributes = __decorate([
    JsonObject("SimpleProductAttributes")
], SimpleProductAttributes);

const AVAILABLE_SOON = "available-soon";
const EXCEPTIONAL_PRODUCT = "exceptional-product";
const OOS_DISPLAY = "oos_display";
const BACK_IN_STOCK_DISPLAY = "back_in_stock_display";
const REGISTER_INTEREST_DISPLAY = "register_your_interest_display";
/**
 * This class describe a Product simple or with Inner
 */
let Product = class Product extends ProductItem {
    constructor() {
        super();
        /**
         * Indicate if there is more colors
         * Not in ProductAttributes because used in grid
         */
        this.moreColors = undefined;
        /** Product size */
        this.size = undefined;
        /**
         * Product mass & volume (for Fragrance & soap)
         * Not in SimpleProductAttributes because used in grid
         */
        this.massVolume = undefined;
        /**
         * Product color
         */
        this.avgColor = undefined;
        /** Is colorless */
        this.isColorless = false;
        /**
         * Product department code
         */
        this.departmentCode = undefined;
        /**
         * Product family code
         */
        this.familyCode = undefined;
        /**
         * Product product code
         */
        this.productCode = undefined;
        /**
         * Product color
         */
        this.divisionCode = undefined;
        /** product price */
        this.price = undefined;
        /** product priceType */
        this.priceType = undefined;
        /** product unitPrice */
        this.unitPrice = undefined;
        /** product unitPriceDesc */
        this.unitPriceDesc = undefined;
        /** product url */
        this.url = undefined;
        /** Display name list */
        this.displayNameList = undefined;
        /** product path */
        this.path = undefined;
        /** product path */
        this.altPaths = undefined;
        /** product slug */
        this.slug = undefined;
        /** Template Type */
        this.templateType = undefined;
        /** Product Set Type */
        this.productSetType = undefined;
        this.productDetails = undefined;
        /** Product Simple attributes */
        this.simpleAttributes = undefined;
        /** Product Beltkit attributes */
        this.beltkitAttributes = undefined;
        /** Product Apple Watch attributes */
        this.appleWatchAttributes = undefined;
        /** Product Gift Set attributes */
        this.giftSetAttributes = undefined;
        /** Product Double Fragrances attributes */
        this.doubleFragranceAttributes = undefined;
        /** Product Look attributes */
        this.lookAttributes = undefined;
        /** Product Bikini attributes */
        this.bikiniAttributes = undefined;
        /** Product Bundle attributes */
        this.bundleAttributes = undefined;
        /** Product variants */
        this.variants = undefined;
        this.content = undefined;
        this.stock = undefined;
        this.shippingDelay = undefined;
        this.leadTime = undefined;
        /** Product categories */
        this.categories = undefined;
        /** Product secondId */
        this.secondId = undefined;
        /** Product personalize */
        this.personalize = undefined;
        /**
         * Product persoProductType
         * Customizable product type (smallleathergoods or beltkits)
         */
        this.persoProductType = undefined;
        /** Product group type */
        this.groupType = undefined;
        /** Product VTO */
        this.isVto = undefined;
        /** Product VTO */
        this.vtoType = undefined;
        /** Label Display Only */
        this.labelDisplayOnly = undefined;
        /** Product Publication Display Mode */
        this.displayMode = undefined;
        /** Has Agec Link */
        this.hasAgecLink = undefined;
        /** Agec Links */
        this.agecLinks = undefined;
        /** Product Categories Breadcrumbs Data */
        this.categoriesBreadcrumbs = undefined;
        /** Product ObjectTypeTitle */
        this.objectTypeTitle = undefined;
        /** Product is Shade Finder */
        this.isShadeFinder = undefined;
    }
    get attributes() {
        switch (this.templateType) {
            case "beltkit": {
                return this.beltkitAttributes;
            }
            case "apple-watch": {
                return this.appleWatchAttributes;
            }
            case "giftset": {
                return this.giftSetAttributes;
            }
            case "doublefragrance": {
                return this.doubleFragranceAttributes;
            }
            case "look": {
                return this.lookAttributes;
            }
            case "bikini": {
                return this.bikiniAttributes;
            }
            case "bundle": {
                return this.bundleAttributes;
            }
            default: {
                return this.simpleAttributes;
            }
        }
    }
};
__decorate([
    JsonProperty("moreColors", Boolean, true),
    __metadata("design:type", Boolean)
], Product.prototype, "moreColors", undefined);
__decorate([
    JsonProperty("size", String, true),
    __metadata("design:type", String)
], Product.prototype, "size", undefined);
__decorate([
    JsonProperty("massVolume", String, true),
    __metadata("design:type", String)
], Product.prototype, "massVolume", undefined);
__decorate([
    JsonProperty("avgColor", String, true),
    __metadata("design:type", String)
], Product.prototype, "avgColor", undefined);
__decorate([
    JsonProperty("isColorless", Boolean, true),
    __metadata("design:type", Boolean)
], Product.prototype, "isColorless", undefined);
__decorate([
    JsonProperty("departmentCode", String, true),
    __metadata("design:type", String)
], Product.prototype, "departmentCode", undefined);
__decorate([
    JsonProperty("familyCode", String, true),
    __metadata("design:type", String)
], Product.prototype, "familyCode", undefined);
__decorate([
    JsonProperty("productCode", String, true),
    __metadata("design:type", String)
], Product.prototype, "productCode", undefined);
__decorate([
    JsonProperty("divisionCode", String, true),
    __metadata("design:type", String)
], Product.prototype, "divisionCode", undefined);
__decorate([
    JsonProperty("price", Number, true),
    __metadata("design:type", Number)
], Product.prototype, "price", undefined);
__decorate([
    JsonProperty("priceType", String, true),
    __metadata("design:type", String)
], Product.prototype, "priceType", undefined);
__decorate([
    JsonProperty("unitPrice", Number, true),
    __metadata("design:type", Number)
], Product.prototype, "unitPrice", undefined);
__decorate([
    JsonProperty("unitPriceDesc", String, true),
    __metadata("design:type", String)
], Product.prototype, "unitPriceDesc", undefined);
__decorate([
    JsonProperty("url", String, true),
    __metadata("design:type", String)
], Product.prototype, "url", undefined);
__decorate([
    JsonProperty("displayNameList", String, true),
    __metadata("design:type", String)
], Product.prototype, "displayNameList", undefined);
__decorate([
    JsonProperty("path", String, true),
    __metadata("design:type", String)
], Product.prototype, "path", undefined);
__decorate([
    JsonProperty("alt_paths", Object, true),
    __metadata("design:type", Object)
], Product.prototype, "altPaths", undefined);
__decorate([
    JsonProperty("slug", String, true),
    __metadata("design:type", String)
], Product.prototype, "slug", undefined);
__decorate([
    JsonProperty("templateType", String, true),
    __metadata("design:type", String)
], Product.prototype, "templateType", undefined);
__decorate([
    JsonProperty("productSetType", String, true),
    __metadata("design:type", String)
], Product.prototype, "productSetType", undefined);
__decorate([
    JsonProperty("productDetails", String, true),
    __metadata("design:type", String)
], Product.prototype, "productDetails", undefined);
__decorate([
    JsonProperty("simpleAttributes", SimpleProductAttributes, true),
    __metadata("design:type", SimpleProductAttributes)
], Product.prototype, "simpleAttributes", undefined);
__decorate([
    JsonProperty("beltkitAttributes", BeltkitProductAttributes, true),
    __metadata("design:type", BeltkitProductAttributes)
], Product.prototype, "beltkitAttributes", undefined);
__decorate([
    JsonProperty("appleWatchAttributes", AppleWatchProductAttributes, true),
    __metadata("design:type", AppleWatchProductAttributes)
], Product.prototype, "appleWatchAttributes", undefined);
__decorate([
    JsonProperty("giftSetAttributes", GiftSetProductAttributes, true),
    __metadata("design:type", GiftSetProductAttributes)
], Product.prototype, "giftSetAttributes", undefined);
__decorate([
    JsonProperty("doubleFragranceAttributes", DoubleFragranceAttributes, true),
    __metadata("design:type", DoubleFragranceAttributes)
], Product.prototype, "doubleFragranceAttributes", undefined);
__decorate([
    JsonProperty("lookAttributes", LookAttributes, true),
    __metadata("design:type", LookAttributes)
], Product.prototype, "lookAttributes", undefined);
__decorate([
    JsonProperty("bikiniAttributes", BikiniAttributes, true),
    __metadata("design:type", BikiniAttributes)
], Product.prototype, "bikiniAttributes", undefined);
__decorate([
    JsonProperty("bundleAttributes", BundleAttributes, true),
    __metadata("design:type", BundleAttributes)
], Product.prototype, "bundleAttributes", undefined);
__decorate([
    JsonProperty("variants", ProductVariants, true),
    __metadata("design:type", ProductVariants)
], Product.prototype, "variants", undefined);
__decorate([
    JsonProperty("content", ProductContent, true),
    __metadata("design:type", ProductContent)
], Product.prototype, "content", undefined);
__decorate([
    JsonProperty("stock", ProductStock, true),
    __metadata("design:type", ProductStock)
], Product.prototype, "stock", undefined);
__decorate([
    JsonProperty("shipping_delay", ShippingDelay, true),
    __metadata("design:type", ShippingDelay)
], Product.prototype, "shippingDelay", undefined);
__decorate([
    JsonProperty("leadTime", ProductLeadTime, true),
    __metadata("design:type", ProductLeadTime)
], Product.prototype, "leadTime", undefined);
__decorate([
    JsonProperty("categories", [String], true),
    __metadata("design:type", Array)
], Product.prototype, "categories", undefined);
__decorate([
    JsonProperty("secondId", String, true),
    __metadata("design:type", String)
], Product.prototype, "secondId", undefined);
__decorate([
    JsonProperty("personalize", Boolean, true),
    __metadata("design:type", Boolean)
], Product.prototype, "personalize", undefined);
__decorate([
    JsonProperty("perso_product_type", String, true),
    __metadata("design:type", String)
], Product.prototype, "persoProductType", undefined);
__decorate([
    JsonProperty("group_type", String, true),
    __metadata("design:type", String)
], Product.prototype, "groupType", undefined);
__decorate([
    JsonProperty("isVto", Boolean, true),
    __metadata("design:type", Boolean)
], Product.prototype, "isVto", undefined);
__decorate([
    JsonProperty("vtoType", String, true),
    __metadata("design:type", String)
], Product.prototype, "vtoType", undefined);
__decorate([
    JsonProperty("labelDisplayOnly", String, true),
    __metadata("design:type", String)
], Product.prototype, "labelDisplayOnly", undefined);
__decorate([
    JsonProperty("displayMode", String, true),
    __metadata("design:type", String)
], Product.prototype, "displayMode", undefined);
__decorate([
    JsonProperty("hasAgecLink", Boolean, true),
    __metadata("design:type", Boolean)
], Product.prototype, "hasAgecLink", undefined);
__decorate([
    JsonProperty("agecLinks", [AgecLink], true),
    __metadata("design:type", Array)
], Product.prototype, "agecLinks", undefined);
__decorate([
    JsonProperty("categories_breadcrumbs", [ProductBreadcrumb], true),
    __metadata("design:type", Array)
], Product.prototype, "categoriesBreadcrumbs", undefined);
__decorate([
    JsonProperty("objectTypeTitle", String, true),
    __metadata("design:type", String)
], Product.prototype, "objectTypeTitle", undefined);
__decorate([
    JsonProperty("isShadeFinder", Boolean, true),
    __metadata("design:type", Boolean)
], Product.prototype, "isShadeFinder", undefined);
Product = __decorate([
    JsonObject("Product"),
    __metadata("design:paramtypes", [])
], Product);

/**
 * This class describe the product items of a giftset
 */
let ProductGiftSet = class ProductGiftSet extends Product {
    constructor() {
        super(...arguments);
        /**
         * items variations.
         */
        this.items = undefined;
    }
};
__decorate([
    JsonProperty("items", [ProductGiftSetItem], true),
    __metadata("design:type", Array)
], ProductGiftSet.prototype, "items", undefined);
ProductGiftSet = __decorate([
    JsonObject("ProductGiftSet")
], ProductGiftSet);

var ProductTemplateType;
(function (ProductTemplateType) {
    ProductTemplateType["Simple"] = "simple";
    ProductTemplateType["Beltkit"] = "beltkit";
    ProductTemplateType["DoubleFragrance"] = "doublefragrance";
    ProductTemplateType["AppleWatch"] = "apple-watch";
    ProductTemplateType["Giftset"] = "giftset";
    ProductTemplateType["PersoSMLG"] = "perso-smlg";
    ProductTemplateType["PersoBeltkit"] = "perso-beltkit";
    ProductTemplateType["Look"] = "look";
    ProductTemplateType["Bikini"] = "bikini";
    ProductTemplateType["Bundle"] = "bundle";
})(ProductTemplateType || (ProductTemplateType = {}));

/**
 * This class describe a grid variant of a GridProduct
 */
let GridVariant = class GridVariant {
    constructor() {
        /**
         * Grid variant sku
         */
        this.sku = undefined;
        /**
         * Grid variant accessibility label
         */
        this.accessibilityLabel = undefined;
        /**
         * Grid variant color code RGB
         */
        this.codeRgb = undefined;
    }
};
__decorate([
    JsonProperty("sku", String, true),
    __metadata("design:type", String)
], GridVariant.prototype, "sku", undefined);
__decorate([
    JsonProperty("accessibilityLabel", String, true),
    __metadata("design:type", String)
], GridVariant.prototype, "accessibilityLabel", undefined);
__decorate([
    JsonProperty("codeRgb", String, true),
    __metadata("design:type", String)
], GridVariant.prototype, "codeRgb", undefined);
GridVariant = __decorate([
    JsonObject("GridVariant")
], GridVariant);

/**
 * This class describe a quick buy properties
 */
let QuickBuyProperties = class QuickBuyProperties {
    constructor() {
        /**
         * Eligible to quick buy
         */
        this.eligible = false;
        /**
         * Variation type
         */
        this.variationType = "";
        /**
         * Product variants
         */
        this.variants = undefined;
    }
};
__decorate([
    JsonProperty("eligible", Boolean, true),
    __metadata("design:type", Boolean)
], QuickBuyProperties.prototype, "eligible", undefined);
__decorate([
    JsonProperty("variationType", String, true),
    __metadata("design:type", String)
], QuickBuyProperties.prototype, "variationType", undefined);
__decorate([
    JsonProperty("variants", ProductVariants, true),
    __metadata("design:type", ProductVariants)
], QuickBuyProperties.prototype, "variants", undefined);
QuickBuyProperties = __decorate([
    JsonObject("QuickBuyProperties")
], QuickBuyProperties);

/**
 * This class describe a Grid Product
 */
let GridProduct = class GridProduct extends Product {
    constructor() {
        super(...arguments);
        /** Grid variants */
        this.gridVariants = undefined;
        /** Quick buy properties */
        this.quickBuyProperties = undefined;
        /** If product is eligible to engraving feature */
        this.isEngravable = undefined;
    }
};
__decorate([
    JsonProperty("gridVariants", [GridVariant], true),
    __metadata("design:type", Array)
], GridProduct.prototype, "gridVariants", undefined);
__decorate([
    JsonProperty("quickBuyProperties", QuickBuyProperties, true),
    __metadata("design:type", QuickBuyProperties)
], GridProduct.prototype, "quickBuyProperties", undefined);
__decorate([
    JsonProperty("isEngravable", Boolean, true),
    __metadata("design:type", Boolean)
], GridProduct.prototype, "isEngravable", undefined);
GridProduct = __decorate([
    JsonObject("GridProduct")
], GridProduct);

var VariationType;
(function (VariationType) {
    VariationType["NO_VARIATION"] = "NO_VARIATION";
    VariationType["COLOR_VARIATION"] = "COLOR_VARIATION";
    VariationType["SIZE_VARIATION"] = "SIZE_VARIATION";
    VariationType["VOLUME_VARIATION"] = "VOLUME_VARIATION";
    VariationType["COLOR_SIZE_VARIATION"] = "COLOR_SIZE_VARIATION";
})(VariationType || (VariationType = {}));

const MEASUREMENT_SYSTEM_INTERNATIONAL = "international";
const MEASUREMENT_SYSTEM_IMPERIAL = "imperial";

export { AVAILABLE_SOON, AbstractProductAttributes, AbstractProductVariant, AgecLink, AppleWatchProductAttributes, BACK_IN_STOCK_DISPLAY, BeltkitProductAttributes, BikiniAttributes, BundleAttributes, CrossSellingItem, CrossSellingProduct, CrossSellingResponse, DoubleFragranceAttributes, EXCEPTIONAL_PRODUCT, GiftSetProductAttributes, GridProduct, GridVariant, LEAD_TIME_FORMAT, LEAD_TIME_PERIOD, LookAttributes, MEASUREMENT_SYSTEM_IMPERIAL, MEASUREMENT_SYSTEM_INTERNATIONAL, OOS_DISPLAY, Product, ProductBreadcrumb, ProductBuckleVariant, ProductColorVariant, ProductContent, ProductEdito, ProductFinishVariant, ProductGiftSet, ProductGiftSetItem, ProductItem, ProductItemBundle, ProductItemBundleBikini, ProductItemLook, ProductLeadTime, ProductLeatherVariant, ProductNeedHelp, ProductNeedHelpCallingHour, ProductNeedHelpContactInfo, ProductSizeVariant, ProductStock, ProductStrapVariant, ProductTemplateType, ProductToggles, ProductVariants, ProductVolumeVariant, ProductWatchStrapVariant, QuickBuyProperties, REGISTER_INTEREST_DISPLAY, ShippingDelay, SimpleProductAttributes, VariationType };
